import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ActivationCheckEmail from '../components/AccountActivation/ActivationCheckEmail';

const secondsUntil = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 1000));
};

// Drives the "Check your email" page: counts down to the next allowed resend
// (the server's exact time, which also covers the hourly cap) and posts the
// resend. The server picks the address from the session, so none is sent.
// With startOverUrl (the onboarding wizard), "Use a different email" forgets
// the pending sign-up and reloads step 1.
export const ActivationCheckEmailPage = ({
  email, nextSendAt: initialNextSendAt, resendUrl, loginUrl, startOverUrl, embedded,
}) => {
  const [nextSendAt, setNextSendAt] = useState(initialNextSendAt);
  const [secondsLeft, setSecondsLeft] = useState(secondsUntil(initialNextSendAt));
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSecondsLeft(secondsUntil(nextSendAt));
    if (!nextSendAt) return undefined;

    const timer = setInterval(() => {
      const left = secondsUntil(nextSendAt);
      setSecondsLeft(left);
      if (left === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [nextSendAt]);

  const onResend = useCallback(async () => {
    setSending(true);
    setNotice(null);
    setError(null);
    try {
      const response = await fetch(resendUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setNextSendAt(data.nextSendAt);
      if (data.sent) {
        setNotice('We sent a new link. Only the newest link works.');
      } else if (!data.pending) {
        // Nothing waiting in this browser: activated elsewhere, or the session expired.
        setError('There is no sign-up waiting for activation here. Try logging in.');
      }
    } catch (e) {
      setError("We couldn't send a new link. Please try again in a minute.");
    } finally {
      setSending(false);
    }
  }, [resendUrl]);

  const onStartOver = useCallback(async () => {
    try {
      const response = await fetch(startOverUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      window.location.assign('/onboarding');
    } catch (e) {
      setError("We couldn't reset the sign-up. Please try again.");
    }
  }, [startOverUrl]);

  return (
    <ActivationCheckEmail
      email={email}
      secondsLeft={secondsLeft}
      sending={sending}
      notice={notice}
      error={error}
      onResend={onResend}
      onStartOver={startOverUrl ? onStartOver : null}
      loginUrl={loginUrl}
      embedded={embedded}
    />
  );
};

ActivationCheckEmailPage.propTypes = {
  email: PropTypes.string,
  nextSendAt: PropTypes.string,
  resendUrl: PropTypes.string.isRequired,
  loginUrl: PropTypes.string.isRequired,
  startOverUrl: PropTypes.string,
  embedded: PropTypes.bool,
};

ActivationCheckEmailPage.defaultProps = {
  email: null,
  nextSendAt: null,
  startOverUrl: null,
  embedded: false,
};

// ReactOnRails 11 calls a registered plain function as a render function, so
// it registers this wrapper rather than the hook component, as
// SucceedoTeamContainer does.
const ActivationCheckEmailContainer = (props) => React.createElement(ActivationCheckEmailPage, props);

export default ActivationCheckEmailContainer;
